import { Component, effect, inject, signal } from '@angular/core';
import { ColDef, GridOptions, type GetDetailRowDataParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../../../error.utils';
import { defaultTableDefOptions } from '../../../../../helpers';
import {
  AssignmentService,
  GetQueuedHistoryRequest,
  QueuedHistoryAssignment,
  QueuedHistoryRun,
} from '../../../../../service/assignment.service';
import { NotifyService } from '../../../../../service/notify.service';

@Component({
  selector: 'app-loss-prevention-admin-queued-assignments',
  templateUrl: './queued-assignments.component.html',
  styleUrl: './queued-assignments.component.scss',
  standalone: false,
})
export class LossPreventionAdminQueuedAssignmentsComponent {
  private assignmentService = inject(AssignmentService);
  private notifyService = inject(NotifyService);

  public isLoadingHistory = signal<boolean>(false);
  private historyQuery = signal<GetQueuedHistoryRequest | undefined>(undefined);

  private readonly historyItemsPerPage = 50;

  private pendingHistoryGetRowsParams: {
    success: (params: { rowData?: QueuedHistoryRun[]; rowCount?: number }) => void;
    fail: () => void;
  } | null = null;

  public readonly historyAssignmentColumns: ColDef<QueuedHistoryAssignment>[] = [
    {
      field: 'review_id',
      headerName: 'Review ID',
      cellDataType: 'text',
      sortable: false,
    },
    {
      field: 'review_subject',
      headerName: 'Subject Type',
      cellDataType: 'text',
      sortable: false,
    },
    {
      field: 'branch_name',
      headerName: 'Branch',
      cellDataType: 'text',
      sortable: false,
    },
    {
      field: 'location_name',
      headerName: 'Location',
      cellDataType: 'text',
      sortable: false,
    },
    {
      field: 'theft_plan',
      headerName: 'Theft Plan',
      cellDataType: 'text',
      sortable: false,
    },
  ];

  private readonly historyAssignmentsDetailOptions: GridOptions<QueuedHistoryAssignment> = {
    suppressCellFocus: true,
    columnDefs: this.historyAssignmentColumns,
    autoSizeStrategy: { type: 'fitGridWidth', defaultMinWidth: 120 },
    defaultColDef: { resizable: false },
  };

  public readonly historyTableOptions: GridOptions<QueuedHistoryRun> = {
    ...defaultTableDefOptions(),
    rowModelType: 'serverSide',
    serverSideInitialRowCount: this.historyItemsPerPage,
    cacheBlockSize: this.historyItemsPerPage,
    maxBlocksInCache: 1,
    pagination: true,
    paginationPageSize: this.historyItemsPerPage,
    domLayout: 'autoHeight',
    masterDetail: true,
    serverSideDatasource: {
      getRows: (params) => {
        const startRow = params.request.startRow ?? 0;
        const page = Math.floor(startRow / this.historyItemsPerPage) + 1;
        this.pendingHistoryGetRowsParams = params;
        this.historyQuery.set({ page, items_per_page: this.historyItemsPerPage });
      },
    },
    getRowId: (params) => {
      const source = params.data?.source ?? 'unknown';
      const runStart = params.data?.run_start_utc;
      return `${source}-${runStart}`;
    },
    detailCellRenderer: 'agDetailCellRenderer',
    detailRowAutoHeight: true,
    isRowMaster: (dataItem) => (dataItem?.assignments.length ?? 0) > 0,
    detailCellRendererParams: {
      detailGridOptions: this.historyAssignmentsDetailOptions,
      getDetailRowData: (params: GetDetailRowDataParams<QueuedHistoryRun, QueuedHistoryAssignment>) => {
        const assignments = params.data?.assignments ?? [];
        params.successCallback(assignments);
      },
    },
  };

  public readonly historyTableColumns: ColDef<QueuedHistoryRun>[] = [
    {
      field: 'source',
      headerName: 'Source',
      cellDataType: 'text',
      sortable: false,
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      field: 'run_start_utc',
      headerName: 'Run Start',
      cellDataType: 'text',
      sortable: false,
      valueFormatter: (params) => this.formatUtcToLocal(params.value),
    },
    {
      field: 'assignments',
      headerName: 'Assignments',
      cellDataType: 'number',
      sortable: false,
      valueGetter: (params) => params.data?.assignments.length ?? 0,
    },
  ];

  constructor() {
    effect(
      () => {
        const historyRequest = this.historyQuery();
        if (!historyRequest) return;

        this.isLoadingHistory.set(true);
        this.assignmentService
          .getQueuedHistory(historyRequest)
          .pipe(finalize(() => this.isLoadingHistory.set(false)))
          .subscribe(
            (response) => {
              if (this.pendingHistoryGetRowsParams) {
                this.pendingHistoryGetRowsParams.success({
                  rowData: response.items,
                  rowCount: response.total_items,
                });
                this.pendingHistoryGetRowsParams = null;
              }
            },
            (err) => {
              this.notifyService.notify(tryGetErrorMessage(err));
              if (this.pendingHistoryGetRowsParams) {
                this.pendingHistoryGetRowsParams.fail();
                this.pendingHistoryGetRowsParams = null;
              }
            },
          );
      },
      { allowSignalWrites: true },
    );
  }

  private formatUtcToLocal(value?: string): string {
    if (!value) return '';
    const parsed = DateTime.fromISO(value, { zone: 'utc' });
    if (!parsed.isValid) return value;
    return parsed.toLocal().toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  }
}
